import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

interface SlideData {
  id: number;
  image: string;
  titleKey: string;
}

const slideAlts: Record<string, string[]> = {
  de: [
    "Geschäftsmann entwickelt eine Strategie für ITK-Business-Development im DACH-Markt.",
    "Geschäftsleute analysieren Marktdaten für strategische ITK-Beratung im DACH-Markt.",
    "Geschäftsmann wählt eine Führungskraft für flexibles ITK-Interim-Management im DACH-Markt.",
    "Geschäftsmann visualisiert Ziele für ITK-Training und Coaching im DACH-Markt.",
  ],
  en: [
    "Businessman develops a strategy for ICT business development in the DACH market.",
    "Businesspeople analyze market data for strategic ICT consulting in the DACH market.",
    "Businessman selects an executive for flexible ICT interim management in the DACH market.",
    "Businessman visualizes goals for ICT training and coaching in the DACH market.",
  ],
  fr: [
    "Un homme d'affaires élabore une stratégie de développement commercial TIC sur le marché DACH.",
    "Des professionnels analysent des données de marché pour le conseil TIC sur le marché DACH.",
    "Un homme d'affaires choisit un cadre pour le management de transition TIC sur le marché DACH.",
    "Un homme d'affaires visualise des objectifs de formation et de coaching TIC sur le marché DACH.",
  ],
  es: [
    "Un empresario desarrolla una estrategia de desarrollo de negocio TIC en el mercado DACH.",
    "Profesionales analizan datos de mercado para consultoría TIC estratégica en el mercado DACH.",
    "Un empresario elige a un directivo para gestión interina TIC flexible en el mercado DACH.",
    "Un empresario visualiza objetivos de formación y coaching TIC en el mercado DACH.",
  ],
};

const slidesData: SlideData[] = [
  {
    id: 1,
    image: "https://net-basis.net/Bilder/ict-business-development-strategy-dach-market-entry.webp",
    titleKey: "overview.slide1_title",
  },
  {
    id: 2,
    image: "https://net-basis.net/Bilder/ict-market-entry-consulting-strategy-dach.webp",
    titleKey: "overview.slide2_title",
  },
  {
    id: 3,
    image: "https://net-basis.net/Bilder/ict-interim-management-experts-dach-market-entry.webp",
    titleKey: "overview.slide3_title",
  },
  {
    id: 4,
    image: "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp",
    titleKey: "overview.slide4_title",
  },
];

function computeDirection(from: number, to: number, total: number): number {
  if (to > from) {
    return (to - from <= total / 2) ? 1 : -1;
  }
  return (from - to <= total / 2) ? -1 : 1;
}

export default function ServicesSlider() {
  const { t, i18n } = useTranslation("services");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState(1);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [transitionActive, setTransitionActive] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [showText, setShowText] = useState(true);
  const [imageHeights, setImageHeights] = useState<Record<number, number>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1100);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slides = slidesData.map((slide) => ({
    ...slide,
    title: t(slide.titleKey),
  }));

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Preload images
  useEffect(() => {
    slidesData.forEach((slide) => {
      const img = new Image();
      img.onload = () => {
        setImageHeights((prev) => ({
          ...prev,
          [slide.id]: img.naturalHeight / img.naturalWidth,
        }));
      };
      img.src = slide.image;
    });
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentSlide) return;

      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      if (textTimerRef.current) clearTimeout(textTimerRef.current);
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);

      const dir = computeDirection(currentSlide, index, slidesData.length);
      setDirection(dir);
      setPrevSlide(currentSlide);
      setCurrentSlide(index);
      setIsTransitioning(true);
      setTransitionActive(false);
      setShowText(false);
      setAnimationKey((prev) => prev + 1);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionActive(true);
        });
      });

      transitionTimerRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setPrevSlide(null);
        setTransitionActive(false);
      }, 3500);

      textTimerRef.current = setTimeout(() => {
        setShowText(true);
      }, 3500);
    },
    [isTransitioning, currentSlide],
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slidesData.length);
  }, [currentSlide, goToSlide]);

  const prevSlideFn = useCallback(() => {
    goToSlide((currentSlide - 1 + slidesData.length) % slidesData.length);
  }, [currentSlide, goToSlide]);

  // Autoplay — 8s per slide
  useEffect(() => {
    if (isTransitioning) return;

    autoplayTimerRef.current = setTimeout(() => {
      nextSlide();
    }, 8000);

    return () => {
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
    };
  }, [currentSlide, isTransitioning, nextSlide]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      if (textTimerRef.current) clearTimeout(textTimerRef.current);
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
    };
  }, []);

  const slide = slides[currentSlide];
  const prevSlideData = prevSlide !== null ? slides[prevSlide] : null;
  const lang = i18n.language in slideAlts ? i18n.language : "de";

  // Calculate container height
  const currentRatio = imageHeights[slide.id];

  const getMinHeight = (): number => {
    const w = containerWidth;
    if (w >= 1024) return 520;
    if (w >= 768) return 420;
    return 280;
  };

  const containerHeight = currentRatio
    ? Math.max(Math.round(containerWidth * currentRatio), getMinHeight())
    : Math.max(Math.round(containerWidth * 0.56), getMinHeight());

  // Slide transform styles
  const outgoingTransform =
    isTransitioning && transitionActive
      ? `translateX(${direction * -100}%)`
      : "translateX(0)";
  const outgoingTransition = transitionActive
    ? "transform 3s ease-in-out, opacity 3s ease-in-out"
    : "none";

  const incomingTransform = isTransitioning
    ? transitionActive
      ? "translateX(0)"
      : `translateX(${direction * 100}%)`
    : "translateX(0)";
  const incomingTransition =
    isTransitioning && transitionActive
      ? "transform 3s ease-in-out, opacity 3s ease-in-out"
      : "none";

  return (
    <section className="w-full bg-white py-4 md:py-6 lg:py-8">
      <div ref={containerRef} className="w-full max-w-[1100px] mx-auto">
        <div
          className="relative w-full overflow-hidden transition-[height] duration-500"
          style={{ height: `${containerHeight}px` }}
          role="region"
          aria-roledescription="carousel"
          aria-label={t("overview.slider_label", { number: 1 }).replace("1", "") || "Slider"}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") { e.preventDefault(); prevSlideFn(); }
            if (e.key === "ArrowRight") { e.preventDefault(); nextSlide(); }
          }}
        >
          {/* OUTGOING slide */}
          {isTransitioning && prevSlideData && (
            <div
              className="absolute inset-0 z-10"
              style={{
                transform: outgoingTransform,
                opacity: isTransitioning && transitionActive ? 0 : 1,
                transition: outgoingTransition,
              }}
            >
              <img loading="lazy"
                src={prevSlideData.image}
                alt={slideAlts[lang]?.[prevSlide ?? 0] || slideAlts.de[prevSlide ?? 0]}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 pointer-events-none" />
            </div>
          )}

          {/* INCOMING / CURRENT slide */}
          <div
            className="absolute inset-0 z-20"
            style={{
              transform: incomingTransform,
              opacity: isTransitioning ? (transitionActive ? 1 : 0) : 1,
              transition: incomingTransition,
            }}
          >
            <img loading="lazy"
              src={slide.image}
              alt={slideAlts[lang]?.[currentSlide] || slideAlts.de[currentSlide]}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 pointer-events-none" />

            {/* Title — loops like home slider */}
            {showText && (
            <div
              className={`absolute inset-0 z-20 flex items-start ${currentSlide === 0 ? 'justify-end' : 'justify-start'} px-6 md:px-10 pointer-events-none`}
            >
              <h2
                key={`${animationKey}-title`}
                className={`text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] ${currentSlide === 0 ? 'text-right' : 'text-left'} mt-5 md:mt-10 lg:mt-10 max-w-[480px] lg:max-w-[540px] ${currentSlide === 0 ? 'services-loop-right-left' : 'services-loop-left-right'} ${currentSlide === 1 ? 'ml-4 md:ml-8 lg:ml-12' : ''}`}
                style={{
                  fontFamily: '"Open Sans", sans-serif',
                }}
              >
                {slide.title}
              </h2>
            </div>
            )}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlideFn}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/25 hover:bg-white/45 backdrop-blur-sm transition-colors cursor-pointer"
            aria-label={t("overview.slider_prev")}
          >
            <i className="ri-arrow-left-s-line text-white text-xl md:text-2xl" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/25 hover:bg-white/45 backdrop-blur-sm transition-colors cursor-pointer"
            aria-label={t("overview.slider_next")}
          >
            <i className="ri-arrow-right-s-line text-white text-xl md:text-2xl" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2 md:gap-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="group w-6 h-6 md:w-7 md:h-7 flex items-center justify-center cursor-pointer"
                aria-label={t("overview.slider_label", { number: index + 1 })}
                aria-current={index === currentSlide ? "true" : "false"}
              >
                <span
                  className={`block w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "bg-primary-400 scale-110"
                      : "bg-white/60 group-hover:bg-white/80"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}